import { useRef, useState } from 'react'
import type { RecipientSummary } from '../../api/types'
import { Empty, Num } from '../../ui/primitives'

/**
 * Proposte per destinatario: due barre per riga, i punti vendita che devono ancora confermare
 * e quelli che hanno già confermato. È l'istogramma della dashboard del sistema precedente, che
 * si legge per capire dove è rimasto lavoro sulla rete; il clic su una riga apre il dettaglio.
 *
 * <p>Due scelte si discostano dall'originale, e vale la pena dire perché.</p>
 *
 * <p>I colori non sono più rosso e verde. Quella coppia è la più difficile che esista per chi ha
 * un deficit di visione dei colori — circa un uomo su dodici: misurata, la distanza fra il rosso
 * e il verde dell'originale è ΔE 5 in deuteranopia, cioè le due barre sono lo stesso colore.
 * Ambra e blu stanno a ΔE 22 e restano distinguibili. In più ogni barra porta il suo numero
 * scritto accanto, quindi l'informazione non è mai affidata al solo colore.</p>
 *
 * <p>Le righe sono ordinate per lavoro residuo invece che per nome: la prima barra è il
 * destinatario su cui conviene intervenire.</p>
 */
export function RecipientChart({
  rows,
  selected,
  onSelect,
}: {
  rows: RecipientSummary[]
  selected?: string
  onSelect: (recipient: RecipientSummary) => void
}) {
  const box = useRef<HTMLDivElement>(null)
  const [hover, setHover] = useState<{ row: RecipientSummary; x: number; y: number; flip: boolean }>()

  if (rows.length === 0) {
    return <Empty>Nessuna proposta nel periodo per i punti vendita selezionati.</Empty>
  }

  const sorted = [...rows].sort(
    (a, b) =>
      b.storesToConfirm - a.storesToConfirm ||
      b.storesConfirmed - a.storesConfirmed ||
      a.recipientName.localeCompare(b.recipientName, 'it'),
  )

  const peak = Math.max(...sorted.map((r) => Math.max(r.storesToConfirm, r.storesConfirmed)))
  const { max, ticks } = niceScale(peak)

  // Il riquadro di riferimento è il grafico, non la riga sotto il cursore: `offsetX` cambia
  // significato a ogni elemento attraversato e il riquadro salterebbe passando su una barra.
  const track = (row: RecipientSummary) => (e: React.MouseEvent) => {
    const rect = box.current?.getBoundingClientRect()
    if (!rect) return
    const x = e.clientX - rect.left
    setHover({ row, x, y: e.clientY - rect.top, flip: x > rect.width * 0.6 })
  }

  return (
    <div className="hbar" ref={box} onMouseLeave={() => setHover(undefined)}>
      <div className="hbar-legend">
        <span className="hbar-key">
          <i className="hbar-swatch is-open" /> Da confermare
        </span>
        <span className="hbar-key">
          <i className="hbar-swatch is-done" /> Confermate
        </span>
      </div>

      <div className="hbar-rows">
        {sorted.map((r) => (
          <button
            key={`${r.channel}~${r.recipientCode}`}
            type="button"
            className={`hbar-row${selected === r.recipientCode ? ' is-selected' : ''}`}
            onClick={() => onSelect(r)}
            onMouseMove={track(r)}
            title={`${r.recipientName}: apri il dettaglio dei punti vendita`}
          >
            <span className="hbar-name">
              <span className="hbar-label">{r.recipientName}</span>
              <small>
                {r.recipientCode}
                {r.channel === 'Cedi' && <span className="hbar-tag">CEDI</span>}
              </small>
            </span>

            <span className="hbar-plot">
              <span className="hbar-grid" aria-hidden="true">
                {ticks.map((t) => (
                  <i key={t} style={{ left: `${(t / max) * 100}%` }} />
                ))}
              </span>

              <Bar value={r.storesToConfirm} max={max} tone="is-open" label="da confermare" />
              <Bar value={r.storesConfirmed} max={max} tone="is-done" label="confermate" />
            </span>
          </button>
        ))}
      </div>

      <div className="hbar-axis" aria-hidden="true">
        <span />
        <span className="hbar-plot">
          {/* Le tacche stanno nello stesso riquadro delle barre e non nel riquadro con il
              margine per i numeri: una percentuale su un elemento posizionato si misura sul
              padding box, quindi senza questo involucro l'ultima tacca cadrebbe più a destra
              della barra piena. */}
          <span className="hbar-scale">
            {ticks.map((t) => (
              <i key={t} style={{ left: `${(t / max) * 100}%` }}>
                {t}
              </i>
            ))}
          </span>
        </span>
      </div>

      <div className="hbar-axis-title" aria-hidden="true">
        <span />
        <span>Punti vendita</span>
      </div>

      {hover && (
        <div
          className={`hbar-tip${hover.flip ? ' is-flipped' : ''}`}
          style={{ left: hover.x, top: hover.y }}
          role="presentation"
        >
          <strong>{hover.row.recipientName}</strong>
          <span>
            Da confermare <b>{hover.row.storesToConfirm}</b>
          </span>
          <span>
            Confermate <b>{hover.row.storesConfirmed}</b>
          </span>
          <em>Clicca per i punti vendita</em>
        </div>
      )}
    </div>
  )
}

function Bar({
  value,
  max,
  tone,
  label,
}: {
  value: number
  max: number
  tone: 'is-open' | 'is-done'
  label: string
}) {
  return (
    <span className="hbar-track">
      <span
        className={`hbar-fill ${tone}${value === 0 ? ' is-empty' : ''}`}
        style={{ width: `${(value / max) * 100}%` }}
        role="img"
        aria-label={`${value} ${label}`}
      />
      <span className={`hbar-value${value === 0 ? ' is-zero' : ''}`}>{value}</span>
    </span>
  )
}

/** Vista tabellare dello stesso dato: serve a chi legge con uno screen reader e a chi stampa. */
export function RecipientTable({
  rows,
  onSelect,
}: {
  rows: RecipientSummary[]
  onSelect: (recipient: RecipientSummary) => void
}) {
  if (rows.length === 0) return <Empty>Nessuna proposta nel periodo.</Empty>

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Destinatario</th>
            <th className="num">Da confermare</th>
            <th className="num">Confermate</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.channel}~${r.recipientCode}`} className="clickable" onClick={() => onSelect(r)}>
              <td>
                <span className="muted">{r.recipientCode}</span> {r.recipientName}
                {r.channel === 'Cedi' && <span className="hbar-tag">CEDI</span>}
              </td>
              <td className="num">
                <Num value={r.storesToConfirm} />
              </td>
              <td className="num">
                <Num value={r.storesConfirmed} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Scala con estremi leggibili. I passi restano interi perché l'unità sono punti vendita, e
 * un'etichetta «2,5 punti vendita» non vuol dire niente.
 */
function niceScale(peak: number) {
  if (peak <= 0) return { max: 1, ticks: [0, 1] }

  const target = peak / 4
  const magnitude = 10 ** Math.floor(Math.log10(target))
  const step =
    [1, 2, 5, 10].map((m) => m * magnitude).find((s) => s >= target && s >= 1) ?? Math.ceil(target)

  const max = Math.ceil(peak / step) * step
  const ticks: number[] = []
  for (let t = 0; t <= max; t += step) ticks.push(t)

  return { max, ticks }
}
